import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, StarIcon, CheckCircleIcon } from 'lucide-react';
import { KeyInsight } from '../components/KeyInsight';
import { InteractiveChecklist } from '../components/InteractiveChecklist';
import { ActionPlan } from '../components/ActionPlan';
import { RecipeCard } from '../components/RecipeCard';
interface BookContentProps {
  onSectionChange: (id: string) => void;
}
export function BookContent({
  onSectionChange
}: BookContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          onSectionChange(entry.target.id);
        }
      });
    }, {
      rootMargin: '-20% 0px -80% 0px'
    });
    const sections = document.querySelectorAll('section[id]');
    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, [onSectionChange]);
  const SectionHeading = ({
    children,
    className = ''
  }: {
    children: React.ReactNode;
    className?: string;
  }) => <h2 className={`text-3xl md:text-4xl font-serif font-medium text-forest mt-16 mb-8 pb-4 border-b border-sage/30 ${className}`} data-id="element-164">
      {children}
    </h2>;
  return <div ref={contentRef} className="mx-auto max-w-[720px] px-6 py-12 md:py-24" data-id="element-165">
      {/* LEGAL DISCLAIMER */}
      <section id="legal" className="mb-16 rounded-xl bg-sage/10 p-6 text-sm text-charcoal/70 italic" data-id="element-166">
        <p className="m-0" data-id="element-167">
          <strong data-id="element-168">Aviso Legal:</strong> Este libro, "Protocolo Anti-Inflamatorio
          65+", se proporciona únicamente con fines informativos y educativos.
          El contenido no pretende sustituir el consejo, diagnóstico o
          tratamiento médico profesional. Siempre busque el consejo de su médico
          u otro proveedor de salud calificado con cualquier pregunta que pueda
          tener con respecto a una condición médica. El autor y el editor no se
          hacen responsables de ningún uso o mal uso de la información contenida
          en este documento. Al leer este libro, usted asume la plena
          responsabilidad de sus decisiones de salud. No ignore el consejo
          médico profesional ni retrase su búsqueda debido a algo que haya leído
          en este protocolo.
        </p>
      </section>

      {/* PERSONAL NOTE */}
      <section id="personal-note" className="mb-24" data-id="element-169">
        <SectionHeading data-id="element-170">Una Nota Personal</SectionHeading>
        <p data-id="element-171">Querida lectora,</p>
        <p data-id="element-172">
          Sé exactamente lo que se siente. Sé lo que es abrir los ojos por la
          mañana, sentir el calor del sol entrando por la ventana, y que el
          primer pensamiento no sea de gratitud, sino de anticipación al dolor.
          Ese momento justo antes de apoyar los pies en el suelo, donde tus
          tobillos se sienten como cristal y tus rodillas pesan como plomo.
        </p>
        <p data-id="element-173">
          Escribí este protocolo porque durante años vi a mujeres increíbles
          —madres, abuelas, profesionales— retirarse de la vida activa no porque
          quisieran, sino porque su cuerpo "ya no les seguía el ritmo". Lo peor
          es que el sistema médico suele decirnos: "Es normal para tu edad".
        </p>
        <p className="font-bold text-forest text-xl my-8 text-center" data-id="element-174">
          Yo me niego a aceptar eso.
        </p>
        <p data-id="element-175">
          Este libro no es una dieta académica. Es una herramienta de rescate.
          He simplificado la ciencia de la nutrición anti-inflamatoria en pasos
          que puedes dar en solo 10 minutos en tu cocina. Mi objetivo no es que
          seas una chef saludable; mi objetivo es que vuelvas a caminar por el
          parque con tus nietos, que vuelvas a subir las escaleras sin dudar y
          que recuperes la libertad que la inflamación te ha robado.
        </p>
        <p data-id="element-176">
          Estás aquí porque sabes que hay una versión de ti que todavía quiere
          bailar, viajar y moverse. Bienvenida. Vamos a recuperarla juntas.
        </p>
      </section>

      {/* INTRODUCTION */}
      <section id="introduction" className="mb-24" data-id="element-177">
        <SectionHeading data-id="element-178">Introducción: El primer paso sin dolor</SectionHeading>
        <p data-id="element-179">
          Eran las 7:15 de la mañana. Carmen se sentó en el borde de su cama,
          como lo hacía cada día, respirando hondo antes de intentar ponerse de
          pie. Sus manos, antes hábiles para el piano y la costura, se sentían
          rígidas y torpes. Sus rodillas emitían un chasquido sordo. Ese primer
          minuto, desde la cama hasta el baño, era una procesión de dolor
          silencioso.
        </p>
        <p data-id="element-180">
          ¿Te suena familiar? Esta escena la viven millones de mujeres cada día.
          Pero aquí está el secreto que nadie te cuenta: No es "solo la edad".
          Es inflamación.
        </p>

        <h3 className="text-2xl mt-10 mb-4" data-id="element-181">
          La Gran Mentira de los Suplementos y los Fármacos
        </h3>
        <p data-id="element-182">
          Probablemente ya has probado de todo. Has comprado botes de colágeno
          carísimos que prometen milagros en la etiqueta pero no cambian nada en
          tus articulaciones. Has tomado analgésicos que te calman el dolor una
          hora pero te irritan el estómago el resto del día.
        </p>
        <p data-id="element-183">
          ¿Por qué fallaron? Porque intentaban apagar un incendio forestal con
          un vaso de agua. La inflamación sistémica no se cura con una pastilla;
          se calma cambiando las señales químicas que le envías a tu cuerpo cada
          vez que comes.
        </p>

        <h3 className="text-2xl mt-10 mb-4" data-id="element-184">
          Qué es este Protocolo (y qué NO es)
        </h3>
        <p data-id="element-185">
          Este no es un libro sobre perder peso (aunque ocurrirá). No es una
          lista de ingredientes exóticos imposibles de encontrar. Este es el
          manual de la Micro-Cocina 65+. Es un sistema diseñado para la mujer
          que tiene una vida ocupada y que no quiere —ni debe— pasar dos horas
          cocinando platos complejos.
        </p>

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-sage/20 my-8" data-id="element-186">
          <h4 className="text-xl font-bold text-forest mb-4" data-id="element-187">
            El Mecanismo Único: El Método S.I.N.
          </h4>
          <p className="mb-4 text-charcoal/80" data-id="element-188">
            Para que este cambio funcione a los 65+, debe ser:
          </p>
          <ul className="space-y-4" data-id="element-189">
            <li className="flex gap-3" data-id="element-190">
              <strong className="text-gold" data-id="element-191">1. Simple:</strong> Ingredientes que
              ya tienes o que compras en el súper de la esquina.
            </li>
            <li className="flex gap-3" data-id="element-192">
              <strong className="text-gold" data-id="element-193">2. Intensivo:</strong> Cada bocado
              debe atacar la inflamación.
            </li>
            <li className="flex gap-3" data-id="element-194">
              <strong className="text-gold" data-id="element-195">3. Nutritivo:</strong> Debe darte
              energía, no quitártela.
            </li>
          </ul>
        </div>
        <p data-id="element-196">
          Este libro te enseñará a usar alimentos comunes como herramientas de
          precisión para "engrasar" tus articulaciones desde adentro. Si puedes
          dedicar 10 minutos a tu cocina, puedes cambiar tu movilidad.
        </p>
      </section>

      {/* CHAPTER 1 */}
      <section id="chapter-1" className="mb-24" data-id="element-197">
        <div className="text-sm font-sans font-bold text-sage uppercase tracking-widest mb-2" data-id="element-198">
          Capítulo 1
        </div>
        <SectionHeading className="mt-0" data-id="element-199">El "Fuego" Invisible</SectionHeading>

        <div className="bg-forest/5 rounded-xl p-6 mb-8" data-id="element-200">
          <h4 className="font-sans font-bold text-forest mb-3" data-id="element-201">
            Objetivo del Capítulo:
          </h4>
          <ul className="list-disc pl-5 space-y-2 text-charcoal/80 text-base" data-id="element-202">
            <li data-id="element-203">Comprender qué es la inflamación crónica.</li>
            <li data-id="element-204">
              Identificar cómo se manifiesta específicamente después de los 65
              años.
            </li>
            <li data-id="element-205">
              Reconocer que el alivio es posible mediante la alimentación.
            </li>
          </ul>
        </div>

        <p data-id="element-206">
          A los 65 años, tu sistema inmune cambia. Se vuelve un poco más
          "sensible". Imagina que tu cuerpo tiene una alarma de incendio. En un
          cuerpo joven, la alarma suena cuando hay una lesión y se apaga rápido.
          En un cuerpo con inflamación crónica, la alarma se queda trabada.
          Suena todo el tiempo, a un volumen bajo pero constante.
        </p>
        <p data-id="element-207">
          Ese sonido constante desgasta tus cartílagos, endurece tus arterias y
          nubla tu mente. Es lo que los científicos llaman ahora "Inflammaging"
          (Inflamación + Envejecimiento).
        </p>

        <KeyInsight data-id="element-208">
          La inflamación no es tu enemiga, es una respuesta de defensa de tu
          cuerpo que simplemente olvidó cómo apagarse. El Protocolo
          Anti-Inflamatorio es el interruptor para apagar esa alarma.
        </KeyInsight>

        <InteractiveChecklist title="Checklist de Reconocimiento:" items={[{
        id: 'c1-1',
        text: 'Siento rigidez en manos o pies al despertar que dura más de 20 minutos.'
      }, {
        id: 'c1-2',
        text: 'Mis articulaciones se sienten "calientes" o hinchadas a veces.'
      }, {
        id: 'c1-3',
        text: 'He notado que ciertos alimentos me hacen sentir pesada o con más dolor.'
      }, {
        id: 'c1-4',
        text: 'Me siento fatigada a pesar de haber dormido 7 u 8 horas.'
      }]} data-id="element-209" />
      </section>

      {/* CHAPTER 2 */}
      <section id="chapter-2" className="mb-24" data-id="element-210">
        <div className="text-sm font-sans font-bold text-sage uppercase tracking-widest mb-2" data-id="element-211">
          Capítulo 2
        </div>
        <SectionHeading className="mt-0" data-id="element-212">
          Los 7 Superalimentos "Escudo" para el Descanso
        </SectionHeading>

        <div className="bg-forest/5 rounded-xl p-6 mb-8" data-id="element-213">
          <h4 className="font-sans font-bold text-forest mb-3" data-id="element-214">
            Objetivo de este capítulo:
          </h4>
          <ul className="list-disc pl-5 space-y-2 text-charcoal/80 text-base" data-id="element-215">
            <li data-id="element-216">
              Identificar los 7 ingredientes que apagan la inflamación de forma
              natural.
            </li>
            <li data-id="element-217">
              Aprender a combinarlos para maximizar su efecto en las
              articulaciones.
            </li>
            <li data-id="element-218">Crear una "armadura" biológica antes de ir a dormir.</li>
          </ul>
        </div>

        <h3 className="text-2xl mt-10 mb-4" data-id="element-219">La farmacia en tu cocina</h3>
        <p data-id="element-220">
          Imagina que cada bocado de tu cena fuera una pequeña dosis de alivio
          para tus rodillas y caderas. No es ciencia ficción; es bioquímica. A
          los 65+, el cuerpo pierde la capacidad de producir ciertos compuestos
          que mantienen los tejidos flexibles. Sin embargo, la naturaleza nos ha
          dado 7 "llaves" que abren la puerta de la regeneración celular
          mientras soñamos. No necesitas suplementos de $50; necesitas saber qué
          poner en tu plato para que tu cuerpo trabaje por ti durante la noche.
        </p>

        <div className="my-12 overflow-hidden rounded-2xl shadow-lg" data-id="element-221">
          <img src="https://cdn.magicpatterns.com/uploads/44t8fezc916uG66x8m3PrU/heylloyd-image-1774933888566.png" alt="Alimentos inflamatorios vs saludables" className="w-full h-auto object-cover" data-id="element-222" />
          <div className="bg-charcoal text-cream p-4 text-sm text-center font-sans" data-id="element-223">
            Lo que pones en tu plato puede ser tu medicina o tu veneno.
          </div>
        </div>

        <h3 className="text-2xl mt-10 mb-6" data-id="element-224">Los 7 Escudos</h3>
        <div className="space-y-6" data-id="element-225">
          <div data-id="element-226">
            <strong className="text-forest text-xl" data-id="element-227">
              1. Cúrcuma con una Pizca de Pimienta:
            </strong>{' '}
            El estándar de oro. La curcumina bloquea las enzimas que causan
            dolor, pero solo se activa si añades pimienta negra. Es como ponerle
            gasolina a un coche.
          </div>
          <div data-id="element-228">
            <strong className="text-forest text-xl" data-id="element-229">
              2. Semillas de Chía o Lino:
            </strong>{' '}
            Ricas en Omega-3 vegetal. Actúan como el aceite que lubrica los
            engranajes de una máquina vieja.
          </div>
          <div data-id="element-230">
            <strong className="text-forest text-xl" data-id="element-231">
              3. Espinacas y Hojas Verdes Oscuras:
            </strong>{' '}
            Cargadas de Magnesio. El magnesio es el relajante muscular de la
            naturaleza; evita los calambres nocturnos y la inquietud de piernas.
          </div>
          <div data-id="element-232">
            <strong className="text-forest text-xl" data-id="element-233">
              4. Pescados Azules Pequeños (Sardinas/Caballa/Salmón):
            </strong>{' '}
            Proteína de fácil digestión que repara el tejido conectivo sin
            inflamar el colon.
          </div>
          <div data-id="element-234">
            <strong className="text-forest text-xl" data-id="element-235">5. Jengibre Fresco:</strong>{' '}
            Un potente analgésico natural que además acelera el vaciado
            gástrico, evitando el reflujo.
          </div>
          <div data-id="element-236">
            <strong className="text-forest text-xl" data-id="element-237">
              6. Nueces de Castilla:
            </strong>{' '}
            Aportan melatonina natural. No solo ayudan a dormir, sino que
            combaten el estrés oxidativo de las articulaciones.
          </div>
          <div data-id="element-238">
            <strong className="text-forest text-xl" data-id="element-239">
              7. Calabacín o Zapallito:
            </strong>{' '}
            El rey de la hidratación celular. Su fibra es tan suave que permite
            que tu sistema digestivo descanse profundamente.
          </div>
        </div>

        <h3 className="text-2xl mt-10 mb-4" data-id="element-240">Método de Combinación Eficaz:</h3>
        <p data-id="element-241">
          Para que el Escudo funcione, cada cena debe tener al menos 3 de estos
          elementos. Por ejemplo: Pescado (Proteína) + Espinacas (Magnesio) +
          Cúrcuma (Anti-inflamatorio).
        </p>

        <KeyInsight data-id="element-242">
          El mayor error es cenar "solo fruta" o "solo un yogur". Esto causa
          picos de insulina que inflaman. Necesitas la grasa buena y la proteína
          suave para sanar.
        </KeyInsight>

        <InteractiveChecklist title="Checklist del Capítulo 2:" items={[{
        id: 'c2-1',
        text: 'Comprar una raíz de jengibre fresca (puedes congelarla).'
      }, {
        id: 'c2-2',
        text: 'Sustituir el aceite de girasol por Aceite de Oliva Virgen Extra (AOVE).'
      }, {
        id: 'c2-3',
        text: 'Tener siempre un bote de cúrcuma y pimienta negra mezcladas sobre la mesa.'
      }, {
        id: 'c2-4',
        text: 'Añadir un puñado de espinacas a cualquier preparación caliente.'
      }, {
        id: 'c2-5',
        text: 'Cambiar el pan blanco por una porción pequeña de nueces si sientes hambre.'
      }]} data-id="element-243" />

        <ActionPlan title="Tu Action Plan: Inventario de Escudos" prompts={[{
        id: 'ap2-1',
        question: '¿Cuáles de estos 7 alimentos ya tienes en casa?'
      }, {
        id: 'ap2-2',
        question: '¿Cuál es el que más te cuesta comer y cómo podrías camuflarlo?'
      }]} data-id="element-244" />
      </section>

      {/* CHAPTER 3 */}
      <section id="chapter-3" className="mb-24" data-id="element-245">
        <div className="text-sm font-sans font-bold text-sage uppercase tracking-widest mb-2" data-id="element-246">
          Capítulo 3
        </div>
        <SectionHeading className="mt-0" data-id="element-247">
          Recetario de 15 Minutos (Lunes a Viernes)
        </SectionHeading>

        <div className="bg-forest/5 rounded-xl p-6 mb-8" data-id="element-248">
          <h4 className="font-sans font-bold text-forest mb-3" data-id="element-249">
            Objetivo de este capítulo:
          </h4>
          <ul className="list-disc pl-5 space-y-2 text-charcoal/80 text-base" data-id="element-250">
            <li data-id="element-251">Ejecutar el Método 5-15-0 con 5 recetas estrella.</li>
            <li data-id="element-252">Minimizar el tiempo de limpieza en la cocina.</li>
            <li data-id="element-253">
              Asegurar una cena deliciosa que huela a hogar y no a "dieta".
            </li>
          </ul>
        </div>

        <h3 className="text-2xl mt-10 mb-4" data-id="element-254">
          El fin de la fatiga en la cocina
        </h3>
        <p data-id="element-255">
          Después de toda una vida cocinando para otros, ahora te toca disfrutar
          del proceso sin el cansancio. Estas recetas están diseñadas para
          ensuciar solo una sartén o un bol. No hay pasos técnicos complicados.
          Si sabes encender el fuego y usar una cuchara, puedes hacer estas
          cenas. El secreto es el orden de los factores: primero el sabor, luego
          la función.
        </p>

        <h3 className="text-2xl mt-10 mb-6" data-id="element-256">Las 5 Cenas de Oro</h3>

        <RecipeCard day="Lunes" title="Salmón al Jengibre 'Relámpago'" ingredients="Filete de salmón, jengibre rallado, espinacas frescas, limón, aceite de oliva." steps="Sella el salmón 4 min por lado. Añade el jengibre y las espinacas al final hasta que reduzcan. Rociar limón." benefit="Omega-3 directo a tus articulaciones." data-id="element-257" />

        <RecipeCard day="Martes" title="Tortilla de Cúrcuma y Calabacín" ingredients="2 huevos, medio calabacín rallado, cúrcuma/pimienta, una pizca de orégano." steps="Saltea el calabacín rallado 3 min. Vierte los huevos batidos con la cúrcuma. Cuaja como una tortilla normal." benefit="Magnesio puro para dormir sin calambres." data-id="element-258" />

        <RecipeCard day="Miércoles" title="Tazón 'Arcoíris' de Garbanzos y Nueces" ingredients="Garbanzos cocidos (de bote, lavados), nueces, hojas verdes, zanahoria rallada, vinagre de manzana." steps="Mezcla todo en un bol. El vinagre de manzana ayuda a que no haya gases." benefit="Proteína vegetal y melatonina." data-id="element-259" />

        <RecipeCard day="Jueves" title="Pechuga de Pollo 'Sedosa' al Limón" ingredients="Filetes de pollo finos, ajo, perejil, mucho limón, puntas de espárragos." steps="Cocina todo junto a fuego medio. El limón 'cocina' ligeramente las fibras para que sean mantequilla en tu estómago." benefit="Fácil digestión, cero reflujo." data-id="element-260" />

        <RecipeCard day="Viernes" title="Sopa 'Crema' de Calabaza Sin Lácteos" ingredients="Calabaza cocida (puede ser de bote natural), leche de coco (una gota), jengibre, semillas de chía." steps="Calentar y triturar con un tenedor. Añadir las semillas al final." benefit="Confort absoluto y desinflamación sistémica." data-id="element-261" />

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-sage/20 my-10" data-id="element-262">
          <h4 className="text-xl font-bold text-forest mb-4" data-id="element-263">
            La Regla de la Mano (Porciones):
          </h4>
          <ul className="space-y-4" data-id="element-264">
            <li className="flex items-center gap-3" data-id="element-265">
              <span className="text-2xl" data-id="element-266">✋</span> Tu palma de la mano =
              Cantidad de proteína.
            </li>
            <li className="flex items-center gap-3" data-id="element-267">
              <span className="text-2xl" data-id="element-268">🤲</span> Tus dos manos juntas =
              Cantidad de vegetales.
            </li>
            <li className="flex items-center gap-3" data-id="element-269">
              <span className="text-2xl" data-id="element-270">👍</span> Tu pulgar = Cantidad de
              grasas buenas (aceite/nueces).
            </li>
          </ul>
        </div>

        <KeyInsight data-id="element-271">
          Si te sientes cansada, usa la "Estrategia del Bote": legumbres o
          verduras ya cocidas al natural son tus mejores aliadas. No tienen
          conservantes malos si las lavas bien.
        </KeyInsight>

        <InteractiveChecklist title="Checklist del Capítulo 3:" items={[{
        id: 'c3-1',
        text: 'Elige una de estas recetas para esta misma noche.'
      }, {
        id: 'c3-2',
        text: 'Prepara los ingredientes sobre la encimera antes de empezar (Mise en place).'
      }, {
        id: 'c3-3',
        text: 'Pon tu música favorita: cocinar feliz también desinflama.'
      }, {
        id: 'c3-4',
        text: 'Mastica cada bocado 20 veces (ayuda a la enzima ptialina).'
      }]} data-id="element-272" />

        <ActionPlan title="Tu Action Plan: Tu Menú Favorito" prompts={[{
        id: 'ap3-1',
        question: 'De las 5 recetas, ¿cuál es la que más te apetece probar?'
      }, {
        id: 'ap3-2',
        question: 'Escribe aquí 3 ingredientes que necesitas comprar mañana:'
      }]} data-id="element-273" />
      </section>

      {/* CHAPTER 4 */}
      <section id="chapter-4" className="mb-24" data-id="element-274">
        <div className="text-sm font-sans font-bold text-sage uppercase tracking-widest mb-2" data-id="element-275">
          Capítulo 4
        </div>
        <SectionHeading className="mt-0" data-id="element-276">
          Postres y Bebidas que Ayudan a Dormir
        </SectionHeading>

        <div className="grid gap-6 md:grid-cols-3 mt-8" data-id="element-277">
          <div className="bg-white p-6 rounded-xl border border-sage/20 shadow-sm" data-id="element-278">
            <h4 className="font-sans font-bold text-forest text-lg mb-2" data-id="element-279">
              La Leche Dorada (Golden Milk)
            </h4>
            <p className="text-base text-charcoal/80 m-0" data-id="element-280">
              Bebida caliente de leche vegetal, cúrcuma y canela.
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl border border-sage/20 shadow-sm" data-id="element-281">
            <h4 className="font-sans font-bold text-forest text-lg mb-2" data-id="element-282">
              Manzanas Asadas al Microondas
            </h4>
            <p className="text-base text-charcoal/80 m-0" data-id="element-283">
              En 3 minutos con canela. Digestión perfecta.
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl border border-sage/20 shadow-sm" data-id="element-284">
            <h4 className="font-sans font-bold text-forest text-lg mb-2" data-id="element-285">
              Infusión de Manzanilla y Jengibre
            </h4>
            <p className="text-base text-charcoal/80 m-0" data-id="element-286">
              El cierre ideal para sellar el estómago.
            </p>
          </div>
        </div>
      </section>

      {/* BACK MATTER */}
      <section id="back-matter" className="mb-24" data-id="element-287">
        <SectionHeading data-id="element-288">Recursos Finales</SectionHeading>

        <h3 className="text-2xl mt-10 mb-6 text-forest" data-id="element-289">
          FAQ (Preguntas Frecuentes)
        </h3>
        <div className="space-y-6" data-id="element-290">
          <div className="bg-white p-6 rounded-xl border border-sage/20" data-id="element-291">
            <h4 className="font-sans font-bold text-lg mb-2" data-id="element-292">
              1. ¿Puedo comer pan en la cena?
            </h4>
            <p className="text-base m-0" data-id="element-293">
              Si es integral o de masa madre y solo una rebanada pequeña, sí.
              Pero prueba a sustituirlo por nueces 3 noches por semana; verás la
              diferencia en tus manos.
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl border border-sage/20" data-id="element-294">
            <h4 className="font-sans font-bold text-lg mb-2" data-id="element-295">
              2. ¿Por qué jengibre si tengo acidez?
            </h4>
            <p className="text-base m-0" data-id="element-296">
              El jengibre en dosis pequeñas ayuda a cerrar la válvula del
              estómago, aunque parezca picante. Si tienes úlcera activa,
              consulta a tu médico.
            </p>
          </div>
        </div>

        <h3 className="text-2xl mt-12 mb-6 text-forest" data-id="element-297">Lista de Compras</h3>
        <div className="bg-sage/10 p-8 rounded-2xl" data-id="element-298">
          <div className="mb-6" data-id="element-299">
            <h4 className="font-sans font-bold text-forest mb-2" data-id="element-300">
              Cesta Básica:
            </h4>
            <p className="m-0" data-id="element-301">
              Cúrcuma, Pimienta, AOVE, Nueces, Huevos ecológicos, Salmón
              congelado, Espinacas baby.
            </p>
          </div>
          <div data-id="element-302">
            <h4 className="font-sans font-bold text-forest mb-2" data-id="element-303">
              Herramienta Útil:
            </h4>
            <p className="m-0" data-id="element-304">
              Rallador fino (para el jengibre y la cúrcuma).
            </p>
          </div>
        </div>

        <div className="mt-16 text-center" data-id="element-305">
          <div className="inline-block w-16 h-1 bg-gold mb-8 rounded-full" data-id="element-306"></div>
          <h3 className="font-serif text-2xl text-forest italic mb-6" data-id="element-307">
            Notas Finales
          </h3>
          <p className="text-xl text-charcoal/80 max-w-2xl mx-auto" data-id="element-308">
            Has dado el paso más importante: decidir que el dolor no es parte de
            tu identidad. Mañana, cuando te levantes y sientas que tus pies
            tocan el suelo con más ligereza, recuerda que ha sido el amor que
            pusiste en tu cena.
          </p>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section id="pricing" className="mb-0 -mx-6 md:-mx-6" data-id="element-309">
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-[#1a1a2e] py-16 px-6 md:px-12" data-id="element-310">
          <div className="max-w-lg mx-auto text-center" data-id="element-311">
            {/* Product Image */}
            <div className="mb-8" data-id="element-312">
              <img src="https://cdn.magicpatterns.com/uploads/1w3SGaC8CKkfWDvGLHfDwh/heylloyd-image-1774330106923.png" alt="Protocolo Anti-Inflamatorio 65+ — Vista del producto digital en tablet" className="w-64 md:w-72 mx-auto rounded-xl" data-id="element-313" />
            </div>

            {/* CTA Button */}
            <motion.a href="https://testeo360.com/cart/51288366874928:1" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.98
          }} className="inline-block w-full max-w-sm bg-[#e8643a] hover:bg-[#d4562e] text-white font-sans font-bold text-lg py-4 px-8 rounded-xl shadow-lg transition-colors duration-200 mb-6 no-underline" data-id="element-314">
              Sí, Quiero "Desinflamarme"
            </motion.a>

            {/* Price */}
            <div className="mb-4 flex items-center justify-center gap-3" data-id="element-315">
              <span className="text-xl text-white/40 line-through font-sans" data-id="element-316">
                $19.99
              </span>
              <span className="text-4xl md:text-5xl font-bold text-white font-sans" data-id="element-317">
                $6.39
              </span>
              <span className="bg-emerald-500 text-white font-sans text-sm font-bold px-3 py-1 rounded-full" data-id="element-318">
                68% OFF
              </span>
            </div>

            <p className="text-white/50 font-sans text-base mb-0" data-id="element-319">
              Acceso inmediato · Pago único y seguro
            </p>
          </div>
        </motion.div>
      </section>
    </div>;
}